import React, { useState, useEffect, useCallback } from 'react';
import { FileData, MarkedRange, ImageAnnotation } from '../../types/training';
import AnnotationToolbar from './AnnotationToolbar';
import TextMarkingEditor from './TextMarkingEditor';
import ImageAnnotationEditor from './ImageAnnotationEditor';
import {
  isTextFile,
  isImageFile,
  isFolder,
  truncateText,
  formatFileSize,
  loadImageAsDataUrl,
  loadTextFile,
} from '../../utils/filePreviewUtils';
import { addTextMark } from '../../utils/markingUtils';
import { addBoundingBox, addPolygon } from '../../utils/annotationUtils';
import './EnhancedFilePreview.css';

interface EnhancedFilePreviewProps {
  file: FileData;
  onFileUpdate: (fileData: FileData) => void;
  onClose: () => void;
  isOpen: boolean;
  embedded?: boolean; // If true, render without overlay/modal styling
}

type FileType = 'text' | 'image' | 'folder' | 'unknown';
type AnnotationTool = 'bbox' | 'polygon';

const EnhancedFilePreview: React.FC<EnhancedFilePreviewProps> = ({
  file,
  onFileUpdate,
  onClose,
  isOpen,
  embedded = false,
}) => {
  const [mode, setMode] = useState<'view' | 'edit'>('view');
  const [fileType, setFileType] = useState<FileType>('unknown');
  const [textContent, setTextContent] = useState<string>('');
  const [imageDataUrl, setImageDataUrl] = useState<string>('');
  const [isLoading, setIsLoading] = useState(false);
  const [selectedColor, setSelectedColor] = useState('#FF6B6B');
  const [selectedTool, setSelectedTool] = useState<AnnotationTool>('bbox');
  const [marks, setMarks] = useState<MarkedRange[]>(file.marks || []);
  const [annotations, setAnnotations] = useState<ImageAnnotation[]>(file.annotations || []);

  // Determine file type and load content
  useEffect(() => {
    if (!isOpen || !file) return;

    const loadContent = async () => {
      setIsLoading(true);

      try {
        if (isTextFile(file.name)) {
          setFileType('text');
          if (file.textContent) {
            const { text } = truncateText(file.textContent);
            setTextContent(text);
          } else if (file.nativeFile) {
            const text = await loadTextFile(file.nativeFile);
            const { text: truncated } = truncateText(text);
            setTextContent(truncated);
          }
        } else if (isImageFile(file.name)) {
          setFileType('image');
          if (file.textContent?.startsWith('data:')) {
            // Already a data URL
            setImageDataUrl(file.textContent);
          } else if (file.textContent && (file.textContent.startsWith('http://') || file.textContent.startsWith('https://'))) {
            // Signed URL from S3
            setImageDataUrl(file.textContent);
          } else if (file.nativeFile) {
            const dataUrl = await loadImageAsDataUrl(file.nativeFile);
            setImageDataUrl(dataUrl);
          } else {
            // No image data available
            setImageDataUrl('');
            console.warn('Image file has no textContent or nativeFile:', file.name);
          }
        } else if (isFolder(file)) {
          setFileType('folder');
          if (file.textContent) {
            setTextContent(file.textContent);
          }
        } else {
          setFileType('unknown');
        }
      } catch (error) {
        console.error('Error loading file:', error);
        setFileType('unknown');
      } finally {
        setIsLoading(false);
      }
    };

    loadContent();
  }, [isOpen, file]);

  // Sync marks and annotations from file
  useEffect(() => {
    if (file.marks) setMarks(file.marks);
    if (file.annotations) setAnnotations(file.annotations);
  }, [file.marks, file.annotations]);

  // Handle text marking
  const handleAddMark = useCallback(
    (start: number, end: number, color: string) => {
      const newMarks = addTextMark(marks, start, end, color);
      setMarks(newMarks);
      onFileUpdate({ ...file, marks: newMarks });
    },
    [marks, file, onFileUpdate]
  );

  const handleRemoveMark = useCallback(
    (markId: string) => {
      const newMarks = marks.filter((m) => m.id !== markId);
      setMarks(newMarks);
      onFileUpdate({ ...file, marks: newMarks });
    },
    [marks, file, onFileUpdate]
  );

  // Handle marks change (for clear all)
  const handleMarksChange = useCallback(
    (newMarks: MarkedRange[]) => {
      setMarks(newMarks);
      onFileUpdate({ ...file, marks: newMarks });
    },
    [file, onFileUpdate]
  );

  // Handle image annotation
  const handleAddAnnotation = useCallback(
    (annotation: ImageAnnotation) => {
      const newAnnotations = [...annotations, annotation];
      setAnnotations(newAnnotations);
      onFileUpdate({ ...file, annotations: newAnnotations });
    },
    [annotations, file, onFileUpdate]
  );

  const handleRemoveAnnotation = useCallback(
    (annotationId: string) => {
      const newAnnotations = annotations.filter((a) => a.id !== annotationId);
      setAnnotations(newAnnotations);
      onFileUpdate({ ...file, annotations: newAnnotations });
    },
    [annotations, file, onFileUpdate]
  );

  // Handle annotations change (for clear all)
  const handleAnnotationsChange = useCallback(
    (newAnnotations: ImageAnnotation[]) => {
      setAnnotations(newAnnotations);
      onFileUpdate({ ...file, annotations: newAnnotations });
    },
    [file, onFileUpdate]
  );

  // Handle save
  const handleSave = useCallback(() => {
    onFileUpdate({
      ...file,
      marks,
      annotations,
    });
    setMode('view');
  }, [marks, annotations, file, onFileUpdate]);

  // Handle cancel
  const handleCancel = useCallback(() => {
    // Revert changes
    setMarks(file.marks || []);
    setAnnotations(file.annotations || []);
    setMode('view');
  }, [file]);

  if (!isOpen) return null;

  if (embedded) {
    return (
      <div className="enhanced-file-preview embedded" style={{ height: '100%', width: '100%' }}>
        {/* Header */}
        <div className="preview-header">
          <div className="header-content">
            <h3>{file.name}</h3>
            <span className="file-size">{formatFileSize(file.size || 0)}</span>
          </div>
          <button className="close-btn" onClick={onClose}>
            ✕
          </button>
        </div>

        {/* Toolbar */}
        {(fileType === 'text' || fileType === 'image') && (
          <AnnotationToolbar
            mode={mode}
            onModeChange={setMode}
            fileType={fileType}
            selectedTool={selectedTool}
            onToolSelect={setSelectedTool as (tool: 'mark' | 'bbox' | 'polygon') => void}
            selectedColor={selectedColor}
            onColorChange={setSelectedColor}
            onClearAll={() => {
              if (fileType === 'text') {
                handleMarksChange([]);
              } else {
                handleAnnotationsChange([]);
              }
            }}
            onSave={handleSave}
            onCancel={handleCancel}
            markCount={marks.length}
            annotationCount={annotations.length}
          />
        )}

        {/* Content Area */}
        <div className="preview-content">
          {isLoading ? (
            <div className="loading-spinner">
              <div className="spinner"></div>
              <p>Loading file...</p>
            </div>
          ) : fileType === 'text' ? (
            <TextMarkingEditor
              text={textContent}
              marks={marks}
              onMarksChange={handleMarksChange}
              selectedColor={selectedColor}
              isEditMode={mode === 'edit'}
              onAddMark={handleAddMark}
              onRemoveMark={handleRemoveMark}
            />
          ) : fileType === 'image' ? (
            <ImageAnnotationEditor
              imageSrc={imageDataUrl}
              annotations={annotations}
              onAnnotationsChange={handleAnnotationsChange}
              selectedColor={selectedColor}
              isEditMode={mode === 'edit'}
              selectedTool={selectedTool}
              onAddAnnotation={handleAddAnnotation}
              onRemoveAnnotation={handleRemoveAnnotation}
            />
          ) : fileType === 'folder' ? (
            <div className="folder-content">
              <pre>{textContent}</pre>
            </div>
          ) : (
            <div className="unsupported-content">
              <p>Preview not available for this file type.</p>
              <p>You can download the file to view it.</p>
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="enhanced-file-preview-overlay" onClick={onClose}>
      <div className="enhanced-file-preview" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="preview-header">
          <div className="header-content">
            <h3>{file.name}</h3>
            <span className="file-size">{formatFileSize(file.size || 0)}</span>
          </div>
          <button className="close-btn" onClick={onClose}>
            ✕
          </button>
        </div>

        {/* Toolbar */}
        {(fileType === 'text' || fileType === 'image') && (
          <AnnotationToolbar
            mode={mode}
            onModeChange={setMode}
            fileType={fileType}
            selectedTool={selectedTool}
            onToolSelect={setSelectedTool as (tool: 'mark' | 'bbox' | 'polygon') => void}
            selectedColor={selectedColor}
            onColorChange={setSelectedColor}
            onClearAll={() => {
              if (fileType === 'text') {
                handleMarksChange([]);
              } else {
                handleAnnotationsChange([]);
              }
            }}
            onSave={handleSave}
            onCancel={handleCancel}
            markCount={marks.length}
            annotationCount={annotations.length}
          />
        )}

        {/* Content Area */}
        <div className="preview-content">
          {isLoading ? (
            <div className="loading-spinner">
              <div className="spinner"></div>
              <p>Loading file...</p>
            </div>
          ) : fileType === 'text' ? (
            <TextMarkingEditor
              text={textContent}
              marks={marks}
              onMarksChange={handleMarksChange}
              selectedColor={selectedColor}
              isEditMode={mode === 'edit'}
              onAddMark={handleAddMark}
              onRemoveMark={handleRemoveMark}
            />
          ) : fileType === 'image' ? (
            <ImageAnnotationEditor
              imageSrc={imageDataUrl}
              annotations={annotations}
              onAnnotationsChange={handleAnnotationsChange}
              selectedColor={selectedColor}
              isEditMode={mode === 'edit'}
              selectedTool={selectedTool}
              onAddAnnotation={handleAddAnnotation}
              onRemoveAnnotation={handleRemoveAnnotation}
            />
          ) : fileType === 'folder' ? (
            <div className="folder-content">
              <pre>{textContent}</pre>
            </div>
          ) : (
            <div className="unsupported-content">
              <p>Preview not available for this file type.</p>
              <p>You can download the file to view it.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default EnhancedFilePreview;
